import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useState } from "react";

import { PaginatedResponse } from "../types";

import { DatasetGroup } from "./types";

import { useAuth } from "@/context/auth-context";
import { jsonAuthHeaders } from "@/utils/headers";
import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";

export const useGetDatasetGroups = ({
  search,
  dataSetId,
  minVersionNumber,
  currentPage,
  is_draft,
}: {
  search?: string;
  dataSetId?: string;
  minVersionNumber?: number;
  currentPage?: number;
  is_draft?: boolean;
}) => {
  const { workspaceId } = useParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const authContext = useAuth();

  const { data, isLoading, refetch } = useQuery<
    PaginatedResponse<DatasetGroup[], "dataset_groups", string>
  >({
    queryKey: [
      "dataset-groups",
      workspaceId,
      search,
      currentPage,
      page,
      pageSize,
      dataSetId,
      minVersionNumber,
      is_draft,
    ],
    queryFn: async () => {
      const url = new URL(ENDPOINTS.get_dataset_groups(Number(workspaceId)));

      if (is_draft !== undefined) {
        url.searchParams.set("is_draft", String(is_draft));
      }

      if (search) {
        url.searchParams.set("q", search);
      }

      if (dataSetId) {
        url.searchParams.set("dataset_id", dataSetId);
      }

      if (minVersionNumber !== undefined) {
        url.searchParams.set("min_version_number", minVersionNumber.toString());
      }

      url.searchParams.set("page", (currentPage ?? page).toString());
      url.searchParams.set("per_page", pageSize.toString());

      const response = await fetch(url, {
        headers: jsonAuthHeaders(authContext!.userToken!),
      });

      return handleResponse(response);
    },
    enabled: !!workspaceId,
  });

  return { data, isLoading, page, setPage, pageSize, setPageSize, refetch };
};
